import { useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { BsDownload } from 'react-icons/bs';
import {
  BoardCertification as BoardCertificationModel,
  Certification as CertificationModel,
  License as LicenseModel,
  LicenseTypeEnum,
  StatusEnum,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { useProvider } from '../../../app/hooks/providers';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { CERTIFICATES_STATES } from '../../../utils/certificates-local-data';

const License = ({
  license,
  index,
  caption = 'License',
  isLoading = false,
}: {
  license?: LicenseModel;
  isLoading?: boolean;
  caption: string;
  index: number;
}) => {
  return (
    <div>
      <span>
        {caption} #{index + 1}
      </span>
      <Row>
        <Col xs={12} lg={6}>
          <Card.Info label="State" isLoading={isLoading}>
            {license?.state
              ? CERTIFICATES_STATES[license.state as keyof typeof CERTIFICATES_STATES] ||
                license.state
              : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={6}>
          <Card.Info label="Number" isLoading={isLoading}>
            {license?.number ? license.number : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={6}>
          <Card.Info label="Type" isLoading={isLoading}>
            {license?.type ? window.LICENSE_TYPES[license.type] : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={6}>
          <Card.Info label="Status" isLoading={isLoading}>
            {license?.status && license.status === StatusEnum.ACTIVE ? '✅' : '❌'}
            {!license?.status && 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={12}>
          <Card.Info label="File" isLoading={isLoading}>
            {license?.file?.file && (
              <Button
                size="sm"
                variant="outline-primary"
                href={license.file.file}
                target="_blank"
                rel="noreferrer"
              >
                <BsDownload className="tw:inline" /> Download License #{index + 1}
              </Button>
            )}
          </Card.Info>
        </Col>
      </Row>
    </div>
  );
};

const BoardCertification = ({
  certification,
  index,
  isLoading = false,
}: {
  certification?: BoardCertificationModel;
  index: number;
  isLoading?: boolean;
}) => {
  return (
    <div>
      <span>License #{index + 1}</span>
      <Row>
        <Col xs={12} lg={6}>
          <Card.Info label="Type" isLoading={isLoading}>
            {certification?.type ? window.BOARD_CERTIFICATION_TYPES[certification.type] : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={6}>
          {certification?.board_eligible && (
            <Card.Info label="Board eligible" isLoading={isLoading}>
              {'✅'}
            </Card.Info>
          )}
        </Col>
        <Col xs={12} lg={12}>
          <Card.Info label="File" isLoading={isLoading}>
            {certification?.file?.file && (
              <Button
                size="sm"
                variant="outline-primary"
                href={certification.file.file}
                target="_blank"
                rel="noreferrer"
              >
                <BsDownload className="tw:inline" /> Download Board Certification #{index + 1}
              </Button>
            )}
          </Card.Info>
        </Col>
      </Row>
    </div>
  );
};

const Certification = ({
  certification,
  index,
  isLoading = false,
}: {
  certification?: CertificationModel;
  index: number;
  isLoading?: boolean;
}) => {
  return (
    <div>
      <span>License #{index + 1}</span>
      <Row>
        <Col xs={12} lg={12}>
          <Card.Info label="Type" isLoading={isLoading}>
            {certification?.type ? window.CERTIFICATION_TYPES[certification.type] : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={12}>
          <Card.Info label="File" isLoading={isLoading}>
            {certification?.file?.file && (
              <Button
                size="sm"
                variant="outline-primary"
                href={certification.file.file}
                target="_blank"
                rel="noreferrer"
              >
                <BsDownload className="tw:inline" /> Download Certification #{index + 1}
              </Button>
            )}
          </Card.Info>
        </Col>
      </Row>
    </div>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Licenses = () => {
  useDocumentTitle('Provider Licenses');
  const { isProviderLoading, provider } = useProvider();

  const medicalLicenses = useMemo(
    () => provider?.licenses?.filter((license) => license.type === LicenseTypeEnum.MEDICAL),
    [provider?.licenses]
  );

  const deaLicenses = useMemo(
    () => provider?.licenses?.filter((license) => license.type === LicenseTypeEnum.DEA),
    [provider?.licenses]
  );

  const csrLicenses = useMemo(
    () => provider?.licenses?.filter((license) => license.type === LicenseTypeEnum.CSR),
    [provider?.licenses]
  );

  return (
    <Row className="align-items-stretch">
      <Col xs={12} lg={6} className="mb-4">
        <Card.SubCard className="p-3 pb-0 mb-3 h-100">
          <div className="p-3">
            <h5 className="m-0">Medical License</h5>
            {isProviderLoading && (
              <>
                <div className="border-top my-3"> </div>
                <License index={0} isLoading caption="Medical License" />
                <div className="border-top my-3"> </div>
                <License index={1} isLoading caption="Medical License" />
              </>
            )}
            {medicalLicenses?.map((license, index) => (
              <div key={'license' + index}>
                <div className="border-top my-3"> </div>
                <License license={license} index={index} caption="Medical License" />
              </div>
            ))}
            {!medicalLicenses?.length && (
              <span className="d-block pt-3">No Medical License registered</span>
            )}
          </div>
        </Card.SubCard>
      </Col>
      <Col xs={12} lg={6} className="mb-4">
        <Card.SubCard className="p-3 pb-0 mb-3 h-100">
          <div className="p-3">
            <h5 className="m-0">DEA License</h5>
            {isProviderLoading && (
              <>
                <div className="border-top my-3"> </div>
                <License index={0} isLoading caption="DEA License" />
                <div className="border-top my-3"> </div>
                <License index={1} isLoading caption="DEA License" />
              </>
            )}
            {deaLicenses?.map((license, index) => (
              <div key={'licenseDCE' + index}>
                <div className="border-top my-3"> </div>
                <License license={license} index={index} caption="DEA License" />
              </div>
            ))}
            {!deaLicenses?.length && (
              <span className="d-block pt-3">No DEA License registered</span>
            )}
          </div>
        </Card.SubCard>
      </Col>
      <Col xs={12} lg={6} className="mb-4">
        <Card.SubCard className="p-3 pb-0 mb-3 h-100">
          <div className="p-3">
            <h5 className="m-0">Board Certifications</h5>
            {isProviderLoading && (
              <>
                <div className="border-top my-3"> </div>
                <BoardCertification index={0} isLoading />
                <div className="border-top my-3"> </div>
                <BoardCertification index={1} isLoading />
              </>
            )}
            {provider?.board_certifications?.map((board_certification, index) => (
              <div key={'licenseDCE' + index}>
                <div className="border-top my-3"> </div>
                <BoardCertification certification={board_certification} index={index} />
              </div>
            ))}
            {!provider?.board_certifications?.length && (
              <span className="d-block pt-3">No Board Certifications registered</span>
            )}
          </div>
        </Card.SubCard>
      </Col>
      <Col xs={12} lg={6} className="mb-4">
        <Card.SubCard className="p-3 pb-0 mb-3 h-100">
          <div className="p-3">
            <h5 className="m-0">Life Support Certifications</h5>
            {isProviderLoading && (
              <>
                <div className="border-top my-3"> </div>
                <Certification index={0} isLoading />
                <div className="border-top my-3"> </div>
                <Certification index={1} isLoading />
              </>
            )}
            {provider?.certifications?.map((certification, index) => (
              <div key={'licenseDCE' + index}>
                <div className="border-top my-3"> </div>
                <Certification certification={certification} index={index} />
              </div>
            ))}
            {!provider?.certifications?.length && (
              <span className="d-block pt-3">No Certifications registered</span>
            )}
          </div>
        </Card.SubCard>
      </Col>
      <Col xs={12} lg={6} className="mb-4">
        <Card.SubCard className="p-3 pb-0 mb-3 h-100">
          <div className="p-3">
            <h5 className="m-0">CSR License</h5>
            {isProviderLoading && (
              <>
                <div className="border-top my-3"> </div>
                <License index={0} isLoading caption="CSR License" />
                <div className="border-top my-3"> </div>
                <License index={1} isLoading caption="CSR License" />
              </>
            )}
            {csrLicenses?.map((license, index) => (
              <div key={'license' + index}>
                <div className="border-top my-3"> </div>
                <License license={license} index={index} caption="CSR License" />
              </div>
            ))}
            {!csrLicenses?.length && (
              <span className="d-block pt-3">No CSR License registered</span>
            )}
          </div>
        </Card.SubCard>
      </Col>
    </Row>
  );
};
